import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLogo } from '../../context/LogoContext';
import { toast } from 'sonner';
import {
  Zap, ArrowLeft, Image, Lock, Sun, Moon, Upload, Save, Volume2, Play,
} from 'lucide-react';
import { PRESET_RINGTONES, previewRingtone, setRingtone, setCustomAudioUrl } from '../../lib/ringtone';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('ems_token')}` });

const HubSettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark, setTheme: applyTheme } = useTheme();
  const { logoUrl: globalLogo, refreshLogo } = useLogo();
  const [settings, setSettings] = useState({
    hub_logo: null, ems_logo: null, mp_logo: null,
    theme: 'dark', hub_name: 'SpillConsult Hub',
    ems_name: 'Exercise Management', mp_name: 'Media Portal',
  });
  const [passwords, setPasswords] = useState({ old_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [uploadingRingtone, setUploadingRingtone] = useState(false);
  const fileRefs = { hub_logo: useRef(), ems_logo: useRef(), mp_logo: useRef() };
  const ringtoneFileRef = useRef();

  useEffect(() => {
    fetch(`${API_URL}/hub/settings`, { headers: getHeaders() })
      .then(r => r.json())
      .then(data => setSettings(prev => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/hub/settings`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({
          theme: settings.theme, hub_name: settings.hub_name,
          ems_name: settings.ems_name, mp_name: settings.mp_name,
          caller_tune: settings.caller_tune,
        }),
      });
      applyTheme(settings.theme);
      // Sync caller tune to ringtone system so PBX uses it globally
      setRingtone(settings.caller_tune || 'classic');
      if (settings.custom_ringtone_url) {
        setCustomAudioUrl(`${BACKEND}${settings.custom_ringtone_url}`);
      }
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (type) => {
    const file = fileRefs[type].current?.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/hub/settings/logo/${type}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('ems_token')}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setSettings(prev => ({ ...prev, [type]: data.url }));
      // Refresh the global logo context so all components get updated
      refreshLogo();
      toast.success('Logo uploaded - applied across all modules');
    } catch (err) { toast.error(err.message || 'Upload failed'); }
  };

  const handleRingtoneUpload = async () => {
    const file = ringtoneFileRef.current?.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB');
      return;
    }
    setUploadingRingtone(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/hub/settings/ringtone`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('ems_token')}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setSettings(prev => ({ ...prev, caller_tune: 'custom', custom_ringtone_url: data.url }));
      setCustomAudioUrl(`${BACKEND}${data.url}`);
      setRingtone('custom');
      toast.success(`Ringtone uploaded (${data.size_kb}KB)`);
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploadingRingtone(false); }
  };

  const handlePreviewTune = (tuneId) => {
    if (tuneId === 'custom' && settings.custom_ringtone_url) {
      setCustomAudioUrl(`${BACKEND}${settings.custom_ringtone_url}`);
    }
    previewRingtone(tuneId);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm) { toast.error('Passwords do not match'); return; }
    setChangingPw(true);
    try {
      const res = await fetch(`${API_URL}/hub/change-password`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ old_password: passwords.old_password, new_password: passwords.new_password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Password changed');
      setPasswords({ old_password: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.message); }
    finally { setChangingPw(false); }
  };

  // Theme-aware styles
  const bg = isDark ? 'bg-[#06070a]' : 'bg-muted';
  const text = isDark ? 'text-white' : 'text-foreground';
  const textMuted = isDark ? 'text-white/40' : 'text-muted-foreground';
  const textSubtle = isDark ? 'text-white/30' : 'text-muted-foreground';
  const textFaintest = isDark ? 'text-white/20' : 'text-muted-foreground';
  const textLabel = isDark ? 'text-white/60' : 'text-muted-foreground';
  const border = isDark ? 'border-white/[0.06]' : 'border-border';
  const cardBg = isDark ? 'bg-white/[0.02]' : 'bg-white';
  const inputBg = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-border text-foreground placeholder:text-muted-foreground';
  const sectionHead = isDark ? 'text-white/60' : 'text-muted-foreground';

  const LogoUploader = ({ type, label }) => (
    <div className={`p-5 rounded-xl border ${border} ${cardBg}`}>
      <p className="text-sm font-medium mb-3">{label}</p>
      <div className="flex items-center gap-4">
        <div className={`w-16 h-16 rounded-xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-muted border-border'} border flex items-center justify-center overflow-hidden`}>
          {settings[type] ? (
            <img src={`${BACKEND}${settings[type]}`} alt={label} className="w-full h-full object-contain" />
          ) : (
            <Image className={`w-6 h-6 ${textFaintest}`} />
          )}
        </div>
        <div>
          <input ref={fileRefs[type]} type="file" accept="image/*" className="hidden" onChange={() => handleLogoUpload(type)} />
          <button onClick={() => fileRefs[type].current?.click()}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors text-xs ${isDark ? 'bg-white/5 border-white/10 hover:border-white/20 text-white/60 hover:text-white' : 'bg-muted border-border hover:border-border text-muted-foreground hover:text-foreground'}`}
            data-testid={`upload-${type}`}>
            <Upload className="w-3.5 h-3.5" /> Upload
          </button>
          <p className={`text-[10px] ${textFaintest} mt-1`}>PNG, JPG up to 5MB</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${bg} ${text}`} data-testid="hub-settings-page">
      {/* Top Bar */}
      <div className={`flex items-center justify-between px-8 py-5 border-b ${border}`}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/hub')} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/5 text-white/40 hover:text-white' : 'hover:bg-muted text-muted-foreground hover:text-foreground'} transition-colors`}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-400" />
            <span className="text-lg font-semibold">Hub Settings</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-8 py-8 space-y-8">
        {/* General */}
        <div>
          <h2 className={`text-sm font-medium ${sectionHead} mb-4`}>General</h2>
          <div className={`p-5 rounded-xl border ${border} ${cardBg} space-y-4`}>
            <div>
              <label className={`text-xs ${textMuted} block mb-1`}>Hub Name</label>
              <input value={settings.hub_name || ''} onChange={e => setSettings({ ...settings, hub_name: e.target.value })}
                className={`w-full ${inputBg} border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50`}
                data-testid="hub-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`text-xs ${textMuted} block mb-1`}>EMS Display Name</label>
                <input value={settings.ems_name || ''} onChange={e => setSettings({ ...settings, ems_name: e.target.value })}
                  placeholder="Exercise Management"
                  className={`w-full ${inputBg} border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50`}
                  data-testid="ems-name-input" />
              </div>
              <div>
                <label className={`text-xs ${textMuted} block mb-1`}>Media Portal Display Name</label>
                <input value={settings.mp_name || ''} onChange={e => setSettings({ ...settings, mp_name: e.target.value })}
                  placeholder="Media Portal"
                  className={`w-full ${inputBg} border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50`}
                  data-testid="mp-name-input" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">Theme</p>
                <p className={`text-xs ${textSubtle}`}>Switch between dark and light mode</p>
              </div>
              <div className="flex gap-2">
                {['dark', 'light'].map(t => (
                  <button key={t} onClick={() => setSettings({ ...settings, theme: t })}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors border ${
                      settings.theme === t
                        ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                        : isDark ? 'bg-white/5 text-white/40 border-white/10 hover:text-white' : 'bg-muted text-muted-foreground border-border hover:text-foreground'
                    }`} data-testid={`theme-${t}`}>
                    {t === 'dark' ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={handleSaveSettings} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm transition-colors disabled:opacity-50"
              data-testid="save-settings-btn">
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>

        {/* Logo */}
        <div>
          <h2 className={`text-sm font-medium ${sectionHead} mb-4`}>Organization Logo</h2>
          <div className={`p-5 rounded-xl border ${border} ${cardBg}`}>
            <p className={`text-xs ${textSubtle} mb-4`}>Upload once — this logo will appear across Hub, EMS, Media Portal, login page, and all user views.</p>
            <div className="flex items-center gap-5">
              <div className={`w-20 h-20 rounded-xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-muted border-border'} border flex items-center justify-center overflow-hidden`}>
                {(globalLogo || settings.hub_logo) ? (
                  <img src={globalLogo || `${BACKEND}${settings.hub_logo}`} alt="Organization Logo" className="w-full h-full object-contain p-1" />
                ) : (
                  <Image className={`w-8 h-8 ${textFaintest}`} />
                )}
              </div>
              <div>
                <input ref={fileRefs.hub_logo} type="file" accept="image/*" className="hidden" onChange={() => handleLogoUpload('hub_logo')} />
                <button onClick={() => fileRefs.hub_logo.current?.click()}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-sm ${isDark ? 'bg-white/5 border-white/10 hover:border-white/20 text-white/60 hover:text-white' : 'bg-muted border-border hover:border-border text-muted-foreground hover:text-foreground'}`}
                  data-testid="upload-hub_logo">
                  <Upload className="w-4 h-4" /> Upload Logo
                </button>
                <p className={`text-[10px] ${textFaintest} mt-1.5`}>PNG, JPG, SVG — max 5MB</p>
              </div>
            </div>
          </div>
        </div>

        {/* Caller Tune — Global Setting */}
        <div>
          <h2 className={`text-sm font-medium ${sectionHead} mb-4`}>Caller Tune</h2>
          <div className={`p-5 rounded-xl border ${border} ${cardBg}`}>
            <p className={`text-xs ${textSubtle} mb-4`}>Select a ringtone that applies to all users during exercises. You can also upload a custom audio file.</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {PRESET_RINGTONES.map(tone => (
                <label
                  key={tone.id}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg cursor-pointer transition-colors border ${
                    settings.caller_tune === tone.id
                      ? 'bg-blue-500/10 border-blue-500/30'
                      : isDark ? 'bg-white/[0.02] border-white/[0.06] hover:border-white/15' : 'bg-white border-border hover:border-blue-300'
                  }`}
                  data-testid={`ringtone-${tone.id}`}
                >
                  <input
                    type="radio"
                    name="caller_tune"
                    checked={settings.caller_tune === tone.id}
                    onChange={() => setSettings({ ...settings, caller_tune: tone.id })}
                    className="accent-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{tone.name}</p>
                    <p className={`text-[10px] ${textSubtle}`}>{tone.description}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); handlePreviewTune(tone.id); }}
                    className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/40 hover:text-white' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}`}
                    title="Preview"
                  >
                    <Play className="w-3.5 h-3.5" />
                  </button>
                </label>
              ))}
            </div>

            {/* Custom ringtone upload */}
            <div className={`p-4 rounded-lg border ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-muted/30'}`}>
              <div className="flex items-center gap-3">
                <label
                  className={`flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer`}
                >
                  <input
                    type="radio"
                    name="caller_tune"
                    checked={settings.caller_tune === 'custom'}
                    onChange={() => { if (settings.custom_ringtone_url) setSettings({ ...settings, caller_tune: 'custom' }); }}
                    className="accent-blue-500"
                    disabled={!settings.custom_ringtone_url}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Custom Audio</p>
                    <p className={`text-[10px] ${textSubtle}`}>
                      {settings.custom_ringtone_url ? 'Custom ringtone uploaded' : 'Upload an audio file below'}
                    </p>
                  </div>
                </label>
                {settings.custom_ringtone_url && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); handlePreviewTune('custom'); }}
                    className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/40 hover:text-white' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}`}
                  >
                    <Play className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="mt-3">
                <input ref={ringtoneFileRef} type="file" accept="audio/*" className="hidden" onChange={handleRingtoneUpload} />
                <button
                  onClick={() => ringtoneFileRef.current?.click()}
                  disabled={uploadingRingtone}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors text-xs ${isDark ? 'bg-white/5 border-white/10 hover:border-white/20 text-white/60 hover:text-white' : 'bg-white border-border hover:border-blue-300 text-muted-foreground hover:text-foreground'} disabled:opacity-50`}
                  data-testid="upload-custom-ringtone"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {uploadingRingtone ? 'Uploading...' : 'Upload Custom Ringtone'}
                </button>
                <p className={`text-[10px] ${textFaintest} mt-1.5`}>MP3, WAV, OGG — max 5MB</p>
              </div>
            </div>
          </div>
        </div>

        {/* Password */}
        <div>
          <h2 className={`text-sm font-medium ${sectionHead} mb-4`}>Change Password</h2>
          <form onSubmit={handleChangePassword} className={`p-5 rounded-xl border ${border} ${cardBg} space-y-3`}>
            <div>
              <label className={`text-xs ${textMuted} block mb-1`}>Current Password</label>
              <input type="password" value={passwords.old_password} onChange={e => setPasswords({ ...passwords, old_password: e.target.value })}
                className={`w-full ${inputBg} border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50`}
                required data-testid="old-password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`text-xs ${textMuted} block mb-1`}>New Password</label>
                <input type="password" value={passwords.new_password} onChange={e => setPasswords({ ...passwords, new_password: e.target.value })}
                  className={`w-full ${inputBg} border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50`}
                  required minLength={6} data-testid="new-password" />
              </div>
              <div>
                <label className={`text-xs ${textMuted} block mb-1`}>Confirm Password</label>
                <input type="password" value={passwords.confirm} onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
                  className={`w-full ${inputBg} border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50`}
                  required data-testid="confirm-password" />
              </div>
            </div>
            <button type="submit" disabled={changingPw}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 ${isDark ? 'bg-white/5 border-white/10 hover:border-white/20 text-white/60 hover:text-white' : 'bg-muted border-border hover:border-border text-muted-foreground hover:text-foreground'}`}
              data-testid="change-password-btn">
              <Lock className="w-4 h-4" /> {changingPw ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HubSettingsPage;
